import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalReducer } from "../../../GlobalContext";
import { getMillisecondsUntilNextQuarterHour } from "../../utils/date";
import { prepareAutomationCommunication } from "../automation/executions/prepareAutomationCommunication";
import {
  createLoadingAutomationDashboardModel,
  DashboardStatus,
  getErrorMessage,
  resolveLoaderResult,
} from "./automationDashboardModel";
import { AutomationDashboardModel, DashboardExecution } from "./automationDashboard.types";

const RefreshIntervalMs = 15 * 60 * 1000;

const failed = (result?: { response?: unknown; status?: number }) =>
  Boolean(result?.response || (result?.status && result.status >= 400));

/** The dashboard's metrics, refreshed on the quarter hour, and the actions on its executions. */
export function useAutomationDashboard(enableAutoRefresh = true) {
  const globalDispatch = useGlobalReducer();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<AutomationDashboardModel>(
    createLoadingAutomationDashboardModel()
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [cancellingExecutionId, setCancellingExecutionId] = useState<string | null>(null);
  const [preparingExecutionId, setPreparingExecutionId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFilteringRuns, setIsFilteringRuns] = useState(false);
  const hasLoadedRef = useRef(false);
  const [recentRunStatus, setRecentRunStatus] = useState("");
  const intervalIdRef = useRef<number | undefined>(undefined);
  const timeoutIdRef = useRef<number | undefined>(undefined);

  const refresh = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) {
        setIsRefreshing(true);
      }

      try {
        const { GetAutomationDashboardMetrics } = await import("../automation/automationService");
        const result = await GetAutomationDashboardMetrics(globalDispatch, recentRunStatus);

        setDashboard(resolveLoaderResult(result));
        setErrorMessage("");
      } catch (error) {
        setDashboard((current) => ({ ...current, status: DashboardStatus.Error }));
        setErrorMessage(getErrorMessage(error));
      } finally {
        if (showSpinner) {
          setIsRefreshing(false);
        }
      }
    },
    [globalDispatch, recentRunStatus]
  );

  const cancelExecution = useCallback(
    async (executionId: string) => {
      setCancellingExecutionId(executionId);
      try {
        const { CancelAutomationExecution } = await import("../automation/automationService");
        const result = await CancelAutomationExecution(null, executionId);

        if (failed(result)) {
          throw result;
        }

        await refresh(false);
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
      } finally {
        setCancellingExecutionId(null);
      }
    },
    [refresh]
  );

  const prepareCommunication = useCallback(
    async (execution: DashboardExecution) => {
      setPreparingExecutionId(execution.id);
      try {
        const result = await prepareAutomationCommunication({
          automationId: execution.automationId,
          fixtureId: execution.fixtureId,
          navigate,
        });

        if (!result.redirected) {
          await refresh(false);
        }
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
      } finally {
        setPreparingExecutionId(null);
      }
    },
    [navigate, refresh]
  );

  useEffect(() => {
    // Only the first read blanks the page; a changed filter reloads the runs in place.
    if (hasLoadedRef.current) {
      setIsFilteringRuns(true);
    } else {
      setDashboard(createLoadingAutomationDashboardModel());
    }

    refresh(false).finally(() => {
      hasLoadedRef.current = true;
      setIsFilteringRuns(false);
    });
  }, [refresh]);

  useEffect(() => {
    if (!enableAutoRefresh) {
      return undefined;
    }

    timeoutIdRef.current = window.setTimeout(() => {
      refresh(false);
      intervalIdRef.current = window.setInterval(() => refresh(false), RefreshIntervalMs);
    }, getMillisecondsUntilNextQuarterHour(new Date()));

    return () => {
      window.clearTimeout(timeoutIdRef.current);
      window.clearInterval(intervalIdRef.current);
    };
  }, [enableAutoRefresh, refresh]);

  return {
    dashboard,
    errorMessage,
    isRefreshing,
    isFilteringRuns,
    recentRunStatus,
    cancellingExecutionId,
    preparingExecutionId,
    setRecentRunStatus,
    refresh,
    cancelExecution,
    prepareCommunication,
  };
}
